import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { App } from "antd";
import { UserAddOutlined } from "@ant-design/icons";
import { formatCommonDict, type CustomerDTO, type BeneficiaryVO, deleteCustomerBeneficiary, getDictLabel, COMMON_DICTS } from "@ifa/utils";
import { useIOSConfirm } from "../../../../../hooks";
import CustomButton from "../../../../../components/CustomButton";
import styles from "./BeneficiarySection.module.css";

type BeneficiarySectionProps = {
  customer: CustomerDTO | null;
  onRefresh?: () => void;
};

export type BeneficiarySectionHandle = {
  // 预留接口
};

export const BeneficiarySection = forwardRef<BeneficiarySectionHandle, BeneficiarySectionProps>(({ customer, onRefresh }, ref) => {
  const { message } = App.useApp();
  const { confirm } = useIOSConfirm();
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // 从 customer.beneficiaries 获取受益人列表
  const beneficiaryList = useMemo(() => {
    return customer?.beneficiaries || [];
  }, [customer?.beneficiaries]);

  useImperativeHandle(ref, () => ({}), []);

  const handleDelete = async (beneficiaryId?: number) => {
    if (!beneficiaryId || !customer?.customerId) {
      // message.error("缺少必要参数");
      return;
    }

    confirm({
      title: "確認刪除",
      content: "您確定要刪除此受益人嗎？此操作無法撤銷。",
      okText: "確認刪除",
      cancelText: "取消",
      onOk: async () => {
        try {
          setDeletingId(beneficiaryId);
          await deleteCustomerBeneficiary(customer.customerId!, beneficiaryId);
          message.success("刪除成功");
          // 刷新客户数据
          onRefresh?.();
        } catch (error: any) {
          console.error("删除受益人失败：", error);
          // message.error(error?.message || "刪除失敗，請稍後重試");
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const handleEdit = (beneficiaryId?: number) => {
    if (!beneficiaryId || !customer?.customerId) {
      return;
    }
    // Navigate to beneficiary edit page
    navigate(`/customers/${customer.customerId}/beneficiaries/${beneficiaryId}/edit`);
  };

  // 辅助函数：掩码身份证号
  const maskIdNumber = (idNum: string | undefined) => {
    if (!idNum) return "-";
    if (idNum.length <= 6) return idNum;
    return idNum.substring(0, 2) + "***" + idNum.substring(idNum.length - 3);
  };

  return (
    <div className={styles.container}>
      {beneficiaryList.length === 0 ? (
        <div className={styles.emptyContainer}>
          <div className={styles.emptyContent}>
            <div className={styles.emptyIcon}>
              <UserAddOutlined />
            </div>
            <div className={styles.emptyTitle}>暫無受益人資料</div>
            <div className={styles.emptyDescription}>預設為法定受益人，若需新增個人受益人，請先新增受益人資料</div>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "20px",
          }}
        >
          {beneficiaryList.map((item, index) => {
            const relationshipText =
              getDictLabel(COMMON_DICTS.relationship, item.relationship) || "-";

            return (
              <div
                key={item.beneficiaryId || index}
                style={{
                  background: "#fff",
                  border: "1px solid #E2E8F0",
                  borderRadius: "10px",
                  padding: "24px",
                  boxShadow: "0px 1px 11px 0px rgba(226, 232, 240, 0.6)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {/* Header: Name and Relationship Badge */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "600",
                        color: "#141923",
                        lineHeight: "27px",
                      }}
                    >
                      {item.basic?.chineseSurname}
                      {item.basic?.chineseGivenName}
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: "bold",
                        color: "#64748B",
                        lineHeight: "19px",
                        textTransform: "uppercase",
                      }}
                    >
                      {item.basic?.englishSurname} {item.basic?.englishGivenName}
                    </div>
                  </div>
                  <div
                    style={{
                      background: "var(--theme-tertiary)",
                      color: "var(--theme-primary)",
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: "20px",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    與受保人關係：{relationshipText}
                  </div>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: "1px",
                    background: "#F1F5F9",
                  }}
                ></div>
                {/* Details: Key-Value Rows */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748B",
                        fontWeight: "500",
                        lineHeight: "17px",
                        width: "120px",
                      }}
                    >
                      受益比例：
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#334255",
                        fontWeight: "500",
                        flex: 1,
                        width: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.percentage ?? 0}%
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748B",
                        fontWeight: "500",
                        lineHeight: "17px",
                        width: "120px",
                      }}
                    >
                      {getDictLabel(COMMON_DICTS.idType, item.basic?.idType) || "證件類型"}：
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#334255",
                        fontWeight: "500",
                        flex: 1,
                        width: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {maskIdNumber(item.basic?.idNumber)}
                    </span>
                  </div>
                </div>

                {/* Buttons */}
                <div style={{ display: "flex", gap: "12px", marginTop: "4px" }}>
                  <CustomButton
                    height="36px"
                    style={{
                      border: "1px solid #E2E8F0",
                      borderRadius: 6,
                      flex: 1,
                    }}
                    bgColor="#fff"
                    color="#666"
                    onClick={() => handleDelete(item.beneficiaryId)}
                    loading={deletingId === item.beneficiaryId}
                    disabled={deletingId !== null}
                  >
                    刪除
                  </CustomButton>
                  <CustomButton
                    height="36px"
                    style={{
                      borderRadius: 6,
                      flex: 1,
                    }}
                    bgColor="var(--theme-primary)"
                    onClick={() => handleEdit(item.beneficiaryId)}
                    disabled={deletingId !== null}
                  >
                    修改
                  </CustomButton>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
